import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listPrograms, createProgram, updateProgram, deleteProgram } from "../../../utils/programHelpers"
import { listCourses } from "../../../utils/courseHelpers"

const PROGRAM_TYPE_OPTIONS = [
  { value: "Program", label: "Program" },
  { value: "Bootcamp", label: "Bootcamp" },
  { value: "Academy", label: "Academy" },
  { value: "Certification", label: "Certification" },
  { value: "Workshop", label: "Workshop" },
  { value: "Training Package", label: "Training Package" },
  { value: "Membership", label: "Membership" },
  { value: "Assessment", label: "Assessment" },
  { value: "Educational Resource", label: "Educational Resource" },
]

const STATUS_VARIANT = { active: "success", archived: "warning" }

// Shared by both Create and Edit — same fields, different submit handler.
const ProgramFormModal = ({ orgId, courses, initialProgram, onClose, onSaved }) => {
  const isEditing = !!initialProgram?.programId
  const [title, setTitle] = useState(initialProgram?.title || "")
  const [description, setDescription] = useState(initialProgram?.description || "")
  const [programType, setProgramType] = useState(initialProgram?.programType || "Program")
  const [deadlineAt, setDeadlineAt] = useState(
    initialProgram?.deadlineAt ? initialProgram.deadlineAt.slice(0, 10) : ""
  )
  const [selectedCourseIds, setSelectedCourseIds] = useState(initialProgram?.courseIds || [])
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState("")

  const toggleCourse = (courseId) => {
    setSelectedCourseIds((prev) =>
      prev?.includes(courseId) ? prev?.filter((id) => id !== courseId) : [...prev, courseId]
    )
  }

  const handleSave = async (e) => {
    e?.preventDefault()
    setError("")
    if (!title?.trim()) {
      setError("Program title is required")
      return
    }

    setIsSaving(true)

    const payload = {
      title,
      description,
      programType,
      deadlineAt: deadlineAt ? new Date(deadlineAt).toISOString() : null,
      courseIds: selectedCourseIds,
    }

    const { error: saveError } = isEditing
      ? await updateProgram(orgId, initialProgram?.programId, payload)
      : await createProgram({ orgId, ...payload })

    setIsSaving(false)

    if (saveError) {
      setError(saveError?.message || "Could not save program")
      return
    }

    onSaved()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="max-w-lg w-full max-h-[90vh] overflow-y-auto dark:bg-card">
        <div className="flex items-center justify-between px-5">
          <h3 className="text-lg font-semibold text-foreground">{isEditing ? "Edit Program" : "New Program"}</h3>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={18} />
        </div>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <Input
              label="Title"
              placeholder="e.g. Web3 Professional Program"
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
              required
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Description</label>
              <textarea
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                rows={3}
                placeholder="What does this program cover?"
                value={description}
                onChange={(e) => setDescription(e?.target?.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Type</label>
              <Select options={PROGRAM_TYPE_OPTIONS} value={programType} onChange={setProgramType} />
            </div>

            <Input
              label="Deadline (optional)"
              type="date"
              value={deadlineAt}
              onChange={(e) => setDeadlineAt(e?.target?.value)}
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Courses in this program ({selectedCourseIds?.length || 0} selected)
              </label>
              {courses?.length === 0 ? (
                <p className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">
                  No courses exist yet — create a course first, then come back to bundle it into this program.
                </p>
              ) : (
                <div className="border border-border rounded-md max-h-48 overflow-y-auto divide-y divide-border">
                  {courses?.map((course) => (
                    <label
                      key={course?.courseId}
                      className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCourseIds?.includes(course?.courseId)}
                        onChange={() => toggleCourse(course?.courseId)}
                        className="rounded border-border"
                      />
                      <span className="flex-1 text-foreground truncate">{course?.title}</span>
                      <Badge variant={course?.status === "published" ? "success" : "warning"} className="shrink-0">
                        {course?.status === "published" ? "Published" : "Draft"}
                      </Badge>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" fullWidth onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="default" fullWidth loading={isSaving}>
                {isEditing ? "Save Changes" : "Create Program"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const ProgramsPanel = ({ organization }) => {
  const navigate = useNavigate()
  const [programs, setPrograms] = useState([])
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingProgram, setEditingProgram] = useState(null)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)

  const loadData = async () => {
    setLoading(true)
    const [{ programs: programList }, { courses: courseList }] = await Promise.all([
      listPrograms(organization?.orgId),
      listCourses(organization?.orgId),
    ])
    setPrograms(programList || [])
    setCourses(courseList || [])
    setLoading(false)
  }

  useEffect(() => {
    if (organization?.orgId) loadData()
  }, [organization?.orgId])

  const handleSaved = () => {
    setShowFormModal(false)
    setEditingProgram(null)
    loadData()
  }

  const handleEdit = (program) => {
    setEditingProgram(program)
    setShowFormModal(true)
  }

  const handleDelete = (programId) => {
    if (!window.confirm("Delete this program? This cannot be undone.")) return
    setPendingDeleteId(programId)
  }

  const confirmDelete = async () => {
    await deleteProgram(organization?.orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadData()
  }

  const courseNameFor = (courseId) => courses?.find((c) => c?.courseId === courseId)?.title

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {programs?.length || 0} program{programs?.length === 1 ? "" : "s"}
        </p>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => {
            setEditingProgram(null)
            setShowFormModal(true)
          }}
        >
          New Program
        </Button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-sm">Loading programs...</div>
      ) : programs?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Layers" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No programs yet</h3>
          <p className="text-muted-foreground text-sm mb-4">
            Bundle existing courses into a program — a Bootcamp, Academy, Certification track, or anything else.
          </p>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => {
              setEditingProgram(null)
              setShowFormModal(true)
            }}
          >
            New Program
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {programs?.map((program) => (
            <Card key={program?.programId} className="p-0 overflow-hidden gap-3 hover:border-accent/40 transition-colors dark:bg-transparent">
              <CardContent className="pt-5 pb-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-foreground text-sm leading-snug line-clamp-2">{program?.title}</h4>
                  <Badge variant={STATUS_VARIANT?.[program?.status] || "success"} className="shrink-0">
                    {program?.status === "archived" ? "Archived" : "Active"}
                  </Badge>
                </div>

                <Badge variant="outline" className="w-fit">
                  {program?.programType || "Program"}
                </Badge>

                {program?.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{program?.description}</p>
                )}

                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="BookOpen" size={12} /> {program?.courseIds?.length || 0} courses
                  </span>
                  {program?.deadlineAt && (
                    <span className="flex items-center gap-1">
                      <Icon name="CalendarClock" size={12} />
                      {new Date(program.deadlineAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>

                {program?.courseIds?.length > 0 && (
                  <p className="text-xs text-muted-foreground truncate">
                    {program?.courseIds
                      ?.map((id) => courseNameFor(id))
                      ?.filter(Boolean)
                      ?.join(", ")}
                  </p>
                )}

                                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="outline" size="sm" fullWidth iconName="Layers" iconPosition="left"
                    onClick={() => navigate(`/organization-program-builder?orgId=${organization?.orgId}&programId=${program?.programId}`)}
                  >
                    Manage
                  </Button>
                  <Button variant="ghost" size="icon" iconName="Pencil" iconSize={16} onClick={() => handleEdit(program)} />
                  <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(program?.programId)} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showFormModal && (
        <ProgramFormModal
          orgId={organization?.orgId}
          courses={courses}
          initialProgram={editingProgram}
          onClose={() => {
            setShowFormModal(false)
            setEditingProgram(null)
          }}
          onSaved={handleSaved}
        />
      )}

      <ReauthModal open={!!pendingDeleteId} label="delete this program" onConfirm={confirmDelete} onClose={() => setPendingDeleteId(null)} />
    </div>
  )
}

export default ProgramsPanel